/**
 * @fileoverview UI state store for app-wide UI management
 * @module stores/ui
 *
 * Uses centralized branding for localStorage key prefix
 * @see src/config/brandingDefaults.ts
 */
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { BRANDING_DEFAULTS } from '@/config/brandingDefaults'

export type ThemeMode = 'light' | 'dark' | 'system'

// Dynamic localStorage key using centralized prefix
const THEME_STORAGE_KEY = `${BRANDING_DEFAULTS.storage.prefix}-${BRANDING_DEFAULTS.storage.themeKey}`

export interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'warning' | 'info'
  timeout?: number
}

/**
 * UI store managing global UI state
 */
export const useUIStore = defineStore('ui', () => {
  // State
  const sidebarOpen = ref(true)
  const sidebarMini = ref(false)
  const themeMode = ref<ThemeMode>('system')
  const toasts = ref<Toast[]>([])
  const globalLoading = ref(false)
  const loadingMessage = ref('')

  // Snackbar state (for v-snackbar binding in DefaultLayout)
  const snackbar = ref({
    show: false,
    message: '',
    color: 'info' as 'success' | 'error' | 'warning' | 'info',
    timeout: 5000
  })

  // Get display for responsive handling
  const display = useDisplay()

  // Getters
  const isMobile = computed(() => display.mobile.value)
  const isTablet = computed(() => display.smAndDown.value && !display.mobile.value)
  const isDesktop = computed(() => display.mdAndUp.value)

  const isDarkMode = computed(() => {
    if (themeMode.value === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return themeMode.value === 'dark'
  })

  const effectiveTheme = computed(() => (isDarkMode.value ? 'dark' : 'light'))

  // Auto-collapse sidebar on mobile
  watch(
    () => display.mobile.value,
    (mobile) => {
      if (mobile) {
        sidebarOpen.value = false
      }
    },
    { immediate: true }
  )

  /**
   * Toggle sidebar visibility
   */
  function toggleSidebar(): void {
    sidebarOpen.value = !sidebarOpen.value
  }

  /**
   * Toggle sidebar mini mode (rail)
   */
  function toggleSidebarMini(): void {
    sidebarMini.value = !sidebarMini.value
  }

  /**
   * Set theme mode
   */
  function setTheme(mode: ThemeMode): void {
    themeMode.value = mode
    localStorage.setItem(THEME_STORAGE_KEY, mode)
  }

  /**
   * Toggle between light and dark mode
   */
  function toggleDarkMode(): void {
    const newMode = isDarkMode.value ? 'light' : 'dark'
    setTheme(newMode)
  }

  /**
   * Load theme from localStorage
   */
  function loadTheme(): void {
    const saved = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode | null
    if (saved && ['light', 'dark', 'system'].includes(saved)) {
      themeMode.value = saved
    }
  }

  /**
   * Show a toast notification
   */
  function showToast(
    message: string,
    type: Toast['type'] = 'info',
    timeout = 5000
  ): string {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const toast: Toast = { id, message, type, timeout }
    toasts.value.push(toast)

    if (timeout > 0) {
      setTimeout(() => {
        dismissToast(id)
      }, timeout)
    }

    return id
  }

  /**
   * Dismiss a toast by ID
   */
  function dismissToast(id: string): void {
    const index = toasts.value.findIndex((t) => t.id === id)
    if (index !== -1) {
      toasts.value.splice(index, 1)
    }
  }

  /**
   * Clear all toasts
   */
  function clearToasts(): void {
    toasts.value = []
  }

  /**
   * Show success toast
   */
  function showSuccess(message: string, timeout?: number): string {
    return showToast(message, 'success', timeout)
  }

  /**
   * Show error toast
   */
  function showError(message: string, timeout?: number): string {
    return showToast(message, 'error', timeout ?? 8000)
  }

  /**
   * Show warning toast
   */
  function showWarning(message: string, timeout?: number): string {
    return showToast(message, 'warning', timeout)
  }

  /**
   * Show info toast
   */
  function showInfo(message: string, timeout?: number): string {
    return showToast(message, 'info', timeout)
  }

  /**
   * Set global loading state
   */
  function setLoading(loading: boolean, message = ''): void {
    globalLoading.value = loading
    loadingMessage.value = message
  }

  /**
   * Show snackbar notification (used by DefaultLayout and many components)
   */
  function showSnackbar(
    message: string,
    color: 'success' | 'error' | 'warning' | 'info' = 'info',
    timeout = 5000
  ): void {
    snackbar.value = {
      show: true,
      message,
      color,
      timeout
    }
  }

  /**
   * Hide snackbar
   */
  function hideSnackbar(): void {
    snackbar.value.show = false
  }

  /**
   * Initialize UI store
   */
  function init(): void {
    loadTheme()

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (themeMode.value === 'system') {
        // Force reactivity update
        themeMode.value = 'system'
      }
    })
  }

  return {
    // State
    sidebarOpen,
    sidebarMini,
    themeMode,
    toasts,
    globalLoading,
    loadingMessage,
    snackbar,
    // Getters
    isMobile,
    isTablet,
    isDesktop,
    isDarkMode,
    effectiveTheme,
    // Actions
    toggleSidebar,
    toggleSidebarMini,
    setTheme,
    toggleDarkMode,
    loadTheme,
    showToast,
    dismissToast,
    clearToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    setLoading,
    showSnackbar,
    hideSnackbar,
    init
  }
})
